import { HttpParams } from "@angular/common/http";
import { AfterViewInit, Component, ContentChildren, ElementRef, forwardRef, Inject, Input, OnDestroy, OnInit, QueryList } from "@angular/core";
import { Subscription } from "rxjs";
import { ErrorMessages } from "../../common/constants";
import { Algorithm, StatisticItem, Thing } from "../../model";
import { AbstractExportContextService } from "../../service/abstract-export-context.service";
import { AlgorithmService } from "../../service/algorithm.service";
import { CustomPropertyService, CustomPropertyType } from "../../service/custom-property.service";
import { DateRangeName, PeriodVariable } from "../../service/date-range.service";
import { FieldService } from "../../service/field.service";
import { StatisticService } from '../../service/statistic.service';
import { WorkSessionDefinitionService } from "../../service/work-session-definition.service";
import { AbstractThingContextService } from "../../shared/class/abstract-thing-context-service.class";
import { StatisticComponent, StatisticResourceType } from "../../shared/component/statistic/statistic.component";
import { LoaderPipe } from "../../shared/pipe";
import { HttpUtility } from "../../utility";
import { ErrorUtility } from "../../utility/error-utility";
import { WidgetPrintResizeCoordinatorService } from "../shared/widget-print-resize-coordinator.service";

@Component({
    selector: 'table-widget',
    template: require('./table-widget.component.html'),
    styles: [require('./table-widget.component.css')]
})
export class TableWidgetComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() title: string;

    @Input() filterEnabled: boolean;

    @Input() exportEnabled: boolean;

    @Input() queryFieldRef: string;

    @Input() categoryFilter: string | Function;

    @Input() valueFilter: string | Function;

    @Input() filterPeriods: string[];

    @Input() exportFileName: string;

    @Input() description: string;

    @Input() showHeaders: boolean = true;

    @Input() periodRef: string;

    @Input() categoryHeader: string;

    @Input() headerFilter: string | Function;

    @Input() period: DateRangeName;

    @ContentChildren(StatisticComponent) statisticComponentInputs: QueryList<StatisticComponent>;

    error: string;
    loaded: boolean = false;
    dataSource: any[] = [];
    displayedColumns: { name: string, label: string, pipeArgument: any, columnFilter: string | Function }[] = [];
    columns: string[];
    subscriberId: string;

    private _unregisterPrintResize: (() => void) | null = null;
    private statisticSubscription: { fieldsName: string[], subscriberId: string };
    private thing: Thing;
    private exportVisibilitySubscription: Subscription;
    private fieldServiceSubscription: Subscription;
    private queryFieldRefTimeoutId: any;
    private statisticComponents: StatisticComponent[];
    private tableGroupByHeaders: { name: string, label: string, pipeArgument: any, calculateTotal: boolean, columnFilter: string | Function }[] = [];
    private algorithmsByWorkSessionDefinitionPostComputationResultField: { [resultField: string]: Algorithm } = {};

    static nextId = 0;

    constructor(
        @Inject(forwardRef(() => FieldService)) private fieldService: FieldService,
        @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => StatisticService)) private statisticService: StatisticService,
        @Inject(forwardRef(() => HttpUtility)) private httpUtility: HttpUtility,
        @Inject(forwardRef(() => LoaderPipe)) private loaderPipe: LoaderPipe,
        @Inject(forwardRef(() => AbstractExportContextService)) private exportService: AbstractExportContextService,
        @Inject(forwardRef(() => ElementRef)) private elementRef: ElementRef,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) private widgetPrintResizeCoordinator: WidgetPrintResizeCoordinatorService,
        @Inject(forwardRef(() => CustomPropertyService)) private customPropertyService: CustomPropertyService,
        @Inject(forwardRef(() => WorkSessionDefinitionService)) private workSessionDefinitionService: WorkSessionDefinitionService,
        @Inject(forwardRef(() => AlgorithmService)) private algorithmService: AlgorithmService
    ) { }

    ngOnInit(): void {
        this._unregisterPrintResize = this.widgetPrintResizeCoordinator.subscribeElement(this.elementRef);
        this.subscriberId = 'table-widget-' + TableWidgetComponent.nextId++;
        if (this.exportEnabled) {
            this.subscribeToExportServices();
        }
        this.filterEnabled = this.filterEnabled || !!this.period;
    }

    ngOnDestroy(): void {
        this._unregisterPrintResize?.();
        this._unregisterPrintResize = null;
        if (this.statisticSubscription) {
            this.fieldService.unsubscribeFromFields(this.statisticSubscription.fieldsName);
        }
        this.exportService.unsubscribeFromExport(this.subscriberId);
        if (this.exportVisibilitySubscription) {
            this.exportVisibilitySubscription.unsubscribe();
        }
        if (this.fieldServiceSubscription) {
            this.fieldServiceSubscription.unsubscribe();
        }
        this.clearQueryFieldRefTimeout();
    }

    ngAfterViewInit(): void {
        if (this.statisticComponentInputs && this.statisticComponentInputs.length) {
            this.statisticComponents = this.statisticComponentInputs.toArray();
            this.thing = this.thingContextService.getCurrentThing();
            let fieldsToSubscribe = [];
            this.periodRef = this.periodRef || this.statisticComponents[0].periodRef;
            if (this.queryFieldRef) {
                fieldsToSubscribe.push(this.queryFieldRef);
            }
            if (this.filterEnabled) {
                fieldsToSubscribe.push(this.subscriberId + '-fromDate');
                fieldsToSubscribe.push(this.subscriberId + '-toDate');
                this.statisticComponents.forEach(statComp => {
                    statComp.startDateFieldRef = this.subscriberId + '-fromDate';
                    statComp.endDateFieldRef = this.subscriberId + '-toDate';
                    statComp.periodRef = null;
                });
            } else if (this.periodRef) {
                fieldsToSubscribe.push(this.periodRef);
                this.statisticComponents.forEach(statComp => {
                    statComp.startDateFieldRef = null;
                    statComp.endDateFieldRef = null;
                    statComp.periodRef = this.periodRef;
                });
            }
            this.getPostComputationAlgorithms().then(() => {
                this.fieldServiceSubscription = this.fieldService.subscribeToFields(fieldsToSubscribe).subscribe(fieldsMap => {
                    this.clearQueryFieldRefTimeout();
                    this.queryFieldRefTimeoutId = setTimeout(() => {
                        this.processStatisticValues(fieldsMap);
                        console.error("Unresolved queryFieldRef");
                    }, 5000);
                    if (!this.queryFieldRef || fieldsMap[this.queryFieldRef] != null) {
                        this.clearQueryFieldRefTimeout();
                        this.processStatisticValues(fieldsMap);
                    }
                });
                this.statisticSubscription = {
                    fieldsName: fieldsToSubscribe,
                    subscriberId: this.subscriberId
                };
            }).catch(err => {
                this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR);
                this.loaded = true;
            });
        } else {
            this.loaded = true;
        }
    }

    private getPostComputationAlgorithms(): Promise<any> {
        let postComputationResultFields: string[] = [];
        this.statisticComponents.forEach(comp => {
            comp.groupBy?.forEach(groupBy => {
                const groupByParts = groupBy.split('.');
                if (groupBy.startsWith("postComputationResults.") && groupByParts.length == 3 && !postComputationResultFields.includes(groupByParts[1])) {
                    postComputationResultFields.push(groupByParts[1]);
                }
            });
        });
        if (postComputationResultFields.length > 0) {
            const params = new HttpParams().set("postComputationOnly", true);
            return this.workSessionDefinitionService.getRecursivelyAllWorkSessionDefinitions(null, null, params).then(wsDefs => {
                let algorithmsToGet: { resultFieldName: string, algorithmId: string }[] = [];
                postComputationResultFields.forEach(resultField => {
                    const wsDef = wsDefs.find(def => def.postComputationAlgorithms && def.postComputationAlgorithms.some(postComputationAlgorithm => postComputationAlgorithm.resultField == resultField));
                    if (wsDef) {
                        const postComputationAlgorithm = (wsDef.postComputationAlgorithms).find(algorithm => algorithm.resultField == resultField);
                        if (postComputationAlgorithm && !algorithmsToGet.some(algorithmToGet => algorithmToGet.resultFieldName == resultField)) {
                            algorithmsToGet.push({ algorithmId: postComputationAlgorithm.algorithmId, resultFieldName: resultField });
                        }
                    }
                });
                const promises = algorithmsToGet.map(algorithmToGet =>
                    this.algorithmService.getAlgorithmById(algorithmToGet.algorithmId)
                        .then(algorithm => {
                            this.algorithmsByWorkSessionDefinitionPostComputationResultField[algorithmToGet.resultFieldName] = algorithm;
                        }).catch(() => null));
                return Promise.all(promises);
            });
        } else {
            return Promise.resolve();
        }
    }

    private processStatisticValues(fieldsMap: any): void {
        let promises = [];
        this.statisticComponents.forEach(statisticComponent => {
            promises.push(this.statisticService.getStatisticValue(statisticComponent, fieldsMap, this.thing, fieldsMap[this.queryFieldRef]).then(value => {
                return { statisticComponent: statisticComponent, value: value };
            }));
        });
        Promise.all(promises).then((results: { statisticComponent: StatisticComponent, value: StatisticItem[] }[]) => {
            this.error = null;
            this.dataSource = [];
            this.tableGroupByHeaders = [];
            results.forEach(result => {
                this.handleStatisticItem(result.value, result.statisticComponent);
            });
            this.buildDisplayColumns();
            if (this.tableGroupByHeaders.some(header => header.calculateTotal) && this.dataSource.length > 1) {
                this.computeTotalsRow();
            }
            this.loaded = true
        }).catch(err => {
            this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR);
            this.loaded = true;
        });
    }

    handleStatisticItem(statisticItems: StatisticItem[], statisticComponent: StatisticComponent): void {
        const hasPeriodGroupBy = statisticComponent.groupBy ? statisticComponent.groupBy.some(el => StatisticService.PERIOD_GROUP_BY_LIST.find(period => period == el) != null) : false;
        if (statisticItems && statisticItems.length > 0) {
            this.buildDataSource(this.statisticService.sortStatisticItems(statisticItems, hasPeriodGroupBy, statisticComponent), statisticComponent);
        }
    }

    private buildDataSource(statisticItems: StatisticItem[], statisticComponent: StatisticComponent): void {
        const calculateTotal = !statisticComponent.aggregation || ['SUM', 'COUNT'].includes(statisticComponent.aggregation);
        if (statisticItems.length == 1 && statisticItems[0].category == "Result") {
            /* Single value no groupBy */
            const header = statisticComponent.label || this.getCategoryFromAggregation(statisticComponent.aggregation);
            let rowObject: any = {};
            rowObject.category = statisticComponent.label || this.statisticService.getStatisticLabel(statisticComponent);
            rowObject[header] = statisticItems[0].value;
            this.mergeDataSource(rowObject);
            if (!this.tableGroupByHeaders.find(el => el.name == header)) {
                this.tableGroupByHeaders.push({ name: header, label: header, pipeArgument: null, calculateTotal: calculateTotal, columnFilter: statisticComponent.filter });
            }
        } else {
            statisticItems.forEach(statisticItem => {
                let tableRowObject = {};
                tableRowObject['category'] = statisticItem.category.toString();
                if (statisticItem.value instanceof Array) {
                    /* Double group-by */
                    statisticItem.value.forEach(item => {
                        const categoryString = item.category.toString();
                        const firstGroupBy = statisticComponent.groupBy?.length ? statisticComponent.groupBy[0] : null;
                        tableRowObject['pipeArgument'] = this.getPipeArgument(statisticComponent, firstGroupBy);
                        if (!this.tableGroupByHeaders.find(el => el.name == categoryString)) {
                            const secondGroupBy = statisticComponent.groupBy?.length > 1 ? statisticComponent.groupBy[1] : null;
                            this.tableGroupByHeaders.push({ name: categoryString, label: categoryString, pipeArgument: this.getPipeArgument(statisticComponent, secondGroupBy), calculateTotal: calculateTotal, columnFilter: statisticComponent.filter });
                        }
                        tableRowObject[categoryString] = item.value;
                    });
                } else {
                    /* Single group-by */
                    const category = statisticComponent.label || this.getCategoryFromAggregation(statisticComponent.aggregation);
                    tableRowObject[category] = statisticItem.value;
                    const groupBy = statisticComponent.groupBy?.length ? statisticComponent.groupBy[0] : null;
                    tableRowObject['pipeArgument'] = this.getPipeArgument(statisticComponent, groupBy);
                    if (!this.tableGroupByHeaders.find(el => el.name == category)) {
                        this.tableGroupByHeaders.push({ name: category, label: category, pipeArgument: null, calculateTotal: calculateTotal, columnFilter: statisticComponent.filter });
                    }
                }
                this.mergeDataSource(tableRowObject);
            });
        }
    }

    private mergeDataSource(newRow: any): void {
        const existingRow = this.dataSource.find(row => row.category == newRow.category);
        if (existingRow) {
            Object.keys(newRow).forEach(key => {
                if (existingRow[key] == null) {
                    existingRow[key] = newRow[key];
                }
            });
        } else {
            this.dataSource.push(newRow);
        }
    }

    private getCategoryFromAggregation(aggregation: string): string {
        switch (aggregation) {
            case "LAST":
                return "VALUE";
            default:
                return aggregation || "COUNT";
        }
    }

    private buildDisplayColumns(): void {
        this.displayedColumns = [{ name: "category", label: this.categoryHeader || "", pipeArgument: null, columnFilter: null }];
        this.displayedColumns = this.displayedColumns.concat(this.tableGroupByHeaders);
        this.columns = this.displayedColumns.map(el => { return el.name });
    }

    export(): void {
        let exportData = "";
        if (this.statisticComponents && this.statisticComponents.length && this.dataSource.length > 0) {
            const columnNames: string[] = this.displayedColumns.map(col => { return col.label });
            exportData = exportData.concat(columnNames.join(','), '\n');
            this.dataSource.forEach(el => {
                let rowData = [];
                this.displayedColumns.forEach(displayCol => {
                    rowData.push(el[displayCol.name] ? el[displayCol.name] : "");
                });
                exportData = exportData.concat(rowData.join(","), "\n");
            });
        }
        let blob = new Blob([exportData]);
        let startDate: number = null;
        let endDate: number = null;
        if (this.filterEnabled) {
            startDate = this.fieldService.getValue(this.subscriberId + '-fromDate');
            endDate = this.fieldService.getValue(this.subscriberId + '-toDate');
        } else if (this.periodRef) {
            const periodVariable: PeriodVariable = this.fieldService.getValue(this.periodRef);
            if (periodVariable && periodVariable.start) {
                startDate = periodVariable.start;
            }
            if (periodVariable && periodVariable.end) {
                endDate = periodVariable.end;
            }
        }
        this.httpUtility.wrapFileAndDownload({ file: blob, fileName: this.exportService.resolveExportFileNamePlaceholders(this.exportFileName, startDate, endDate) || this.exportService.getDefaultFileName(this.title, "data") });
    }

    private subscribeToExportServices(): void {
        this.exportService.subscribeToExport(this.subscriberId, this.title || "Table Widget").subscribe(() => this.export());
        this.exportVisibilitySubscription = this.exportService.getIsExportButtonPresent().subscribe(isExportButtonPresent => {
            this.exportEnabled = !isExportButtonPresent;
        });
    }

    private clearQueryFieldRefTimeout(): void {
        if (this.queryFieldRefTimeoutId) {
            clearTimeout(this.queryFieldRefTimeoutId);
            this.queryFieldRefTimeoutId = null;
        }
    }

    private getPipeArgument(statisticComponent: StatisticComponent, groupBy: string): any {
        const index = groupBy.indexOf("properties.");
        if (index > -1) {
            const customPropertyType = this.getCustomPropertyType(statisticComponent, groupBy);
            const propertyName = groupBy.substring(index + 11);
            const propertyDef = customPropertyType ? this.customPropertyService.getCustomPropertyDefinitionByTypeAndName(customPropertyType, propertyName) : null;
            return propertyDef ? { property: propertyDef } : null
        } else if (groupBy.startsWith("postComputationResults.") && groupBy.split('.').length == 3) {
            const groupByParts = groupBy.split('.');
            const algorithm = this.algorithmsByWorkSessionDefinitionPostComputationResultField[groupByParts[1]];
            const output = algorithm.outputs?.find(o => o.name == groupByParts[2]);
            return output ? { property: output } : null;
        }
        return null;
    }

    private getCustomPropertyType(statisticComponent: StatisticComponent, groupBy: string): CustomPropertyType {
        if (groupBy.startsWith("customer.")) {
            return CustomPropertyType.Customer;
        } else if (groupBy.startsWith("location.")) {
            return CustomPropertyType.Location;
        } else if (groupBy.startsWith("thing.")) {
            return CustomPropertyType.Thing;
        } else if (groupBy.startsWith("thingDefinition.")) {
            return CustomPropertyType.ThingDefinition;
        } else if (groupBy.startsWith("partner.")) {
            return CustomPropertyType.Partner;
        } else {
            switch (statisticComponent.resource) {
                case StatisticResourceType.ACTION:
                    return CustomPropertyType.Action;
                case StatisticResourceType.ALERT:
                case StatisticResourceType.HISTORICAL_ALERT:
                case StatisticResourceType.EVENT:
                    return null;
                case StatisticResourceType.CUSTOMER:
                    return CustomPropertyType.Customer;
                case StatisticResourceType.LOCATION:
                    return CustomPropertyType.Location;
                case StatisticResourceType.WORK_SESSION:
                case StatisticResourceType.HISTORICAL_WORK_SESSION:
                    return CustomPropertyType.WorkSession;
                case StatisticResourceType.PARTNER:
                    return CustomPropertyType.Partner;
                case StatisticResourceType.THING:
                    return CustomPropertyType.Thing;
                case StatisticResourceType.USER:
                    return CustomPropertyType.User;
                default:
                    return CustomPropertyType.Thing;
            }
        }
    }

    private computeTotalsRow(): void {
        let totalsRow = {};
        totalsRow['category'] = "Total";
        totalsRow['isTotalsRow'] = true;
        this.tableGroupByHeaders.forEach(header => {
            const headerName = header.name;
            if (!header.calculateTotal || this.dataSource.some(d => d[headerName] != null && isNaN(d[headerName]))) {
                totalsRow[headerName] = '-';
            } else {
                let sum: number = 0;
                this.dataSource.forEach(d => {
                    if (d[headerName] != null) {
                        sum += d[headerName];
                    }
                });
                totalsRow[headerName] = sum;
            }
        });
        this.dataSource.push(totalsRow);
    }

}